import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { OrderStatus } from "@/components/portal/OrderStatus";
import { SubscriptionManagement } from "@/components/portal/SubscriptionManagement";
import { CareInstructions } from "@/components/portal/CareInstructions";
import {
  Mail,
  HelpCircle,
  Home,
  AlertCircle,
  Sprout,
  Package,
  CreditCard,
  Leaf,
  ArrowRight,
  Sparkles
} from "lucide-react";
import { motion } from "framer-motion";
import type { PortalData } from "@shared/schema";
import { stripHtml, isAlphanumericSafe } from "@/lib/sanitize";
import { OptimizedBackgroundImage } from "@/components/ui/optimized-image";
import heroImage from "@assets/generated_images/moss_forest_terrarium.png";

function PortalHeader() {
  return (
    <header
      role="banner"
      className="border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-50"
    >
      <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
        <a
          href="/"
          className="font-serif text-xl font-semibold tracking-tight"
          aria-label="Life Itself Studio - Return to homepage"
        >
          <span className="text-primary">Life Itself</span> Studio
        </a>
        <span className="text-sm text-muted-foreground" aria-label="You are in the Customer Portal">Customer Portal</span>
      </div>
    </header>
  );
}

function ContactSection() {
  return (
    <Card role="complementary" aria-labelledby="contact-section-title">
      <CardHeader>
        <CardTitle id="contact-section-title" className="font-serif text-xl flex items-center gap-2">
          <HelpCircle className="w-5 h-5" aria-hidden="true" />
          Questions About Your Order?
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground">
          We're here to help! If you have any questions about your terrarium order,
          care tips, or anything else, don't hesitate to reach out.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href="mailto:hello@lifeitself.studio"
            className="flex items-center gap-2 text-primary hover:underline"
            aria-label="Send email to hello@lifeitself.studio"
          >
            <Mail className="w-4 h-4" aria-hidden="true" />
            hello@lifeitself.studio
          </a>
        </div>

        <Separator />

        <nav className="flex flex-col sm:flex-row gap-4 pt-2" aria-label="Quick links">
          <Button variant="outline" asChild>
            <a href="/" aria-label="Return to Life Itself Studio homepage">
              <Home className="w-4 h-4 mr-2" aria-hidden="true" />
              Back to Home
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href="/#faq" aria-label="View frequently asked questions">
              <HelpCircle className="w-4 h-4 mr-2" aria-hidden="true" />
              View FAQ
            </a>
          </Button>
        </nav>
      </CardContent>
    </Card>
  );
}

function PortalSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}

function PortalError({ message }: { message: string }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" role="alert" aria-live="assertive">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-destructive mb-4" aria-hidden="true" />
          <h2 className="text-xl font-serif font-semibold mb-2">Unable to Load Portal</h2>
          <p className="text-muted-foreground mb-6">{message}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" asChild>
              <a href="/" aria-label="Return to Life Itself Studio homepage">
                <Home className="w-4 h-4 mr-2" aria-hidden="true" />
                Go Home
              </a>
            </Button>
            <Button asChild>
              <a href="mailto:hello@lifeitself.studio" aria-label="Contact support via email">
                <Mail className="w-4 h-4 mr-2" aria-hidden="true" />
                Contact Support
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const portalFeatures = [
  {
    icon: Package,
    title: "Track Your Order",
    description: "Follow your terrarium's journey from our studio to your doorstep with real-time updates."
  },
  {
    icon: CreditCard,
    title: "Manage Subscription",
    description: "Easily update payment methods, view billing history, or adjust your care subscription."
  },
  {
    icon: Leaf,
    title: "Care Instructions",
    description: "Access personalized care guides to keep your living creation thriving for years to come."
  }
];

function PortalLanding() {
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <OptimizedBackgroundImage
          src={heroImage}
          className="absolute inset-0"
          backgroundSize="cover"
          backgroundPosition="center"
          aria-hidden="true"
        >
          <div className="absolute inset-0" />
        </OptimizedBackgroundImage>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-background" aria-hidden="true" />

        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-white/90">Customer Portal</span>
            </div>

            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 leading-tight">
              Your Living Creation<br />
              <span className="text-emerald-400">Awaits</span>
            </h1>

            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl mx-auto">
              Track orders, manage subscriptions, and discover care tips—all in one place.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Button
                size="lg"
                onClick={() => setLocation("/checkout")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-500/40 hover:scale-105"
              >
                <Sprout className="w-5 h-5 mr-2" />
                Design Your Terrarium
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2"
          >
            <div className="w-1.5 h-1.5 bg-white/60 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">
              What You Can Do Here
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Once you've ordered your terrarium, you'll receive a personal portal link to access all these features.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {portalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-b from-card to-card/50">
                  <CardContent className="pt-8 pb-8 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-5">
                      <feature.icon className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="font-serif text-xl font-semibold mb-3">{feature.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-emerald-50 to-background dark:from-emerald-950/20 dark:to-background">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center"
        >
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">
            Ready to Create Your Living Terrarium?
          </h2>
          <p className="text-muted-foreground text-lg mb-8">
            Design a custom terrarium and get your personal portal link to track every step of the journey.
          </p>
          <Button
            size="lg"
            onClick={() => setLocation("/checkout")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-500/40"
          >
            <Sprout className="w-5 h-5 mr-2" />
            Start Designing
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </section>

      {/* Help Section for Existing Customers */}
      <section className="py-16 px-4 border-t border-border">
        <div className="max-w-2xl mx-auto">
          <Card className="border-dashed">
            <CardContent className="pt-6 text-center">
              <HelpCircle className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-serif text-xl font-semibold mb-2">Already Ordered?</h3>
              <p className="text-muted-foreground mb-6">
                Check your confirmation email for your personal portal link, or reach out and we'll help you find it.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button variant="outline" asChild>
                  <a href="/">
                    <Home className="w-4 h-4 mr-2" />
                    Back to Home
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="mailto:hello@lifeitself.studio">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Support
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Life Itself Studio. All rights reserved.</p>
          <p className="mt-2">Handcrafted terrariums made with love in Colorado.</p>
        </div>
      </footer>
    </div>
  );
}

export default function Portal() {
  const params = useParams<{ token: string }>();
  const token = params?.token || "";

  const { data: portalData, isLoading, error } = useQuery<PortalData>({
    queryKey: [`/api/portal/${token}`],
    // Validate token format before making request - prevents XSS via URL parameter
    enabled: !!token && token.length >= 32 && isAlphanumericSafe(token),
    retry: false,
  });

  // No token - show beautiful landing page directing users to design their terrarium
  if (!token) {
    return <PortalLanding />;
  }

  // Invalid token format - validate alphanumeric to prevent XSS
  if (token.length < 32 || !isAlphanumericSafe(token)) {
    return (
      <div className="min-h-screen bg-background">
        <PortalHeader />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <PortalError message="This portal link doesn't look right. Please check your confirmation email for the correct link." />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PortalHeader />

      <main className="max-w-4xl mx-auto px-4 py-8">
        {isLoading ? (
          <PortalSkeleton />
        ) : error ? (
          <PortalError
            message="We couldn't find your order. This link may have expired or the order may not exist. Please check your confirmation email or contact support."
          />
        ) : portalData ? (
          <div className="space-y-6">
            {/* Welcome Message */}
            <div className="mb-8">
              <h1 className="font-serif text-3xl font-semibold mb-2">
                Welcome, {stripHtml(portalData.order.name)}!
              </h1>
              <p className="text-muted-foreground">
                Track your order, manage your subscription, and find care tips for your terrarium.
              </p>
            </div>

            {/* Order Status */}
            <OrderStatus order={portalData.order} />

            {/* Subscription Management */}
            <SubscriptionManagement
              subscription={portalData.subscription}
              billingHistory={portalData.billingHistory}
              token={token}
            />

            {/* Care Instructions */}
            <CareInstructions
              terrariumStyle={portalData.order.terrariumStyle}
              hasActiveSubscription={portalData.subscription?.status === "active"}
              token={token}
            />

            {/* Contact Section */}
            <ContactSection />
          </div>
        ) : (
          <PortalError message="Something went wrong. Please try again later." />
        )}
      </main>

      {/* Footer */}
      <footer role="contentinfo" className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Life Itself Studio. All rights reserved.</p>
          <p className="mt-2">
            Handcrafted terrariums made with love in Colorado.
          </p>
        </div>
      </footer>
    </div>
  );
}
